// StickyReveal.jsx
import React, { useRef, useEffect, useState } from "react";
import "./StickyReveal.css";

/**
 * StickyReveal component permite ajustar la altura scrollable
 * mediante el prop `scrollHeight` (en vh).
 * - videoSrc: URL o import del video
 * - imageSrc: URL o import de la imagen
 * - maxScale: escala inicial máxima (ej: 3 = 300%)
 * - scrollHeight: altura del contenedor en vh (ej: 300 = 300vh)
 */
const StickyReveal = ({ videoSrc, imageSrc, maxScale, scrollHeight }) => {
  const containerRef = useRef(null);
  const [reveal, setReveal] = useState(0); // porcentaje de revelado (0–1)

  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return;
      const { top, height } = containerRef.current.getBoundingClientRect();
      const windowH = window.innerHeight;
      const totalScroll = height - windowH;
      const scrolled = windowH - top;
      let pct = scrolled / totalScroll;
      pct = Math.min(Math.max(pct, 0), 1);
      setReveal(pct);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Zoom dinámico: al inicio (reveal=0) scale = maxScale, al final (reveal=1) scale = 1
  // Easing suave con cubic curve y offset
  const easing = (x) => Math.pow(x, 1.5); // más suave que lineal
  const zoomStartAt = 0.1; // empieza a hacer zoom cuando reveal > 10%

  const adjustedReveal = Math.max(
    0,
    (reveal - zoomStartAt) / (1 - zoomStartAt)
  );
  const easedReveal = easing(adjustedReveal);
  const scale = maxScale - easedReveal * (maxScale - 1);

  return (
    <div
      ref={containerRef}
      className="sr-container"
      style={{ height: `${scrollHeight}vh` }}
    >
      <div className="sr-sticky">
        <video
          className="sr-video"
          src={videoSrc}
          autoPlay
          muted
          loop
          playsInline
        />
        <img
          className="sr-overlay"
          src={imageSrc}
          alt="Overlay"
          style={{
            clipPath: `inset(0 0 ${(1 - reveal) * 100}% 0)`, // revela imagen
            transform: `scale(${scale})`, // zoom dinámico
          }}
        />
      </div>
    </div>
  );
};

export default StickyReveal;
